import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Lock, Unlock, CheckCircle2, AlertTriangle, IndianRupee, Package as PackageIcon,
  Calendar, History, ArrowUpCircle,
} from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";
import { RazorpayFeesCalculator } from "@/components/RazorpayFeesCalculator";

/** Status badge colors keyed by computed status string from /api/subscription/admin-info. */
const STATUS_TONE = {
  locked:   { color: "bg-red-500/20 text-red-300 border-red-500/40",       label: "LOCKED",   icon: Lock },
  expired:  { color: "bg-red-500/20 text-red-300 border-red-500/40",       label: "EXPIRED",  icon: AlertTriangle },
  grace:    { color: "bg-amber-500/20 text-amber-300 border-amber-500/40", label: "GRACE",    icon: AlertTriangle },
  due_soon: { color: "bg-amber-500/20 text-amber-300 border-amber-500/40", label: "DUE SOON", icon: AlertTriangle },
  active:   { color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", label: "ACTIVE", icon: CheckCircle2 },
};

const fmtINR = (n) => `INR ${Number(n || 0).toLocaleString("en-IN")}`;

const AdminSubscription = () => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const { refresh } = useSubscription();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);

  // Pricing form
  const [priceCore, setPriceCore] = useState("");
  const [pricePro, setPricePro] = useState("");

  // Mark-paid form
  const [paymentAmt, setPaymentAmt] = useState("");
  const [paymentNote, setPaymentNote] = useState("");

  const fetchInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/subscription/admin-info`);
      setInfo(data);
      setPriceCore(String(data.pricing_core ?? ""));
      setPricePro(String(data.pricing_pro ?? ""));
      setPaymentAmt(String(data.monthly_amount ?? ""));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load subscription info — master access required.");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchInfo(); }, []); // eslint-disable-line

  const markPaid = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/subscription/mark-paid`, {
        amount: paymentAmt ? Number(paymentAmt) : undefined,
        note: paymentNote,
      });
      toast.success("Payment recorded — extended by 1 month");
      setPaymentNote("");
      await fetchInfo(); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to record payment"); }
    finally { setBusy(false); }
  };

  const lockApp = async () => {
    if (!window.confirm("Lock the entire app? All edits will be blocked until you unlock.")) return;
    setBusy(true);
    try { await axios.post(`${API}/subscription/lock`); toast.success("App locked"); await fetchInfo(); refresh(); }
    catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const unlockApp = async () => {
    setBusy(true);
    try { await axios.post(`${API}/subscription/unlock`); toast.success("App unlocked"); await fetchInfo(); refresh(); }
    catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const updatePricing = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/subscription/update-pricing`, {
        pricing_core: priceCore !== "" ? Number(priceCore) : undefined,
        pricing_pro:  pricePro  !== "" ? Number(pricePro)  : undefined,
      });
      toast.success("Pricing saved");
      await fetchInfo(); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const changePlan = async (target) => {
    if (!target || target === info.package) return;
    const isUpgrade = info.package === "core" && target === "pro";
    const msg = isUpgrade
      ? "Upgrade this customer from CORE to PRO? Pro-only modules will become available immediately."
      : "Downgrade this customer from PRO to CORE? Pro-only modules will be hidden.";
    if (!window.confirm(msg)) return;
    setPlanBusy(true);
    try {
      await axios.post(`${API}/subscription/change-plan`, { package: target });
      toast.success(`Plan changed to ${target.toUpperCase()}`);
      await fetchInfo(); refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change plan");
    } finally { setPlanBusy(false); }
  };

  if (loading) return <div className="p-6 text-gray-300">Loading subscription info…</div>;
  if (!info) return <div className="p-6 text-red-400">Vendor access required.</div>;

  const status = info.status || "active";
  const tone = STATUS_TONE[status] || STATUS_TONE.active;
  const StatusIcon = tone.icon;
  const dueDate = info.next_due_date ? new Date(info.next_due_date) : null;
  const dueText = dueDate ? dueDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

  // Banner message based on status
  const banner =
    status === "expired" ? `Subscription expired ${info.days_overdue} day(s) ago. App locked for edits.` :
    status === "grace"   ? `Subscription overdue by ${info.days_overdue} day(s). App auto-locks in ${5 - info.days_overdue} day(s).` :
    status === "due_soon"? `Payment due in ${info.days_until_due} day(s) (on ${dueText}).` :
    status === "locked"  ? `App is manually locked by master.` :
    null;

  const BannerIcon = (status === "active") ? CheckCircle2 : AlertTriangle;
  const bannerColor =
    status === "expired" || status === "locked" ? "bg-red-500/10 border-red-500/40 text-red-200" :
    status === "grace" || status === "due_soon" ? "bg-amber-500/10 border-amber-500/40 text-amber-200" :
    "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-5">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <PackageIcon className="h-6 w-6 text-orange-400" /> Subscription
          </h1>
          <p className="text-sm text-gray-400">Vendor only · Monthly billing — payments due on the 1st of each month</p>
        </div>

        {/* Status banner (only shown when not active) */}
        {banner && (
          <div className={`rounded-lg border px-4 py-3 flex items-center gap-2 ${bannerColor}`} data-testid="status-banner">
            <BannerIcon className="h-4 w-4 flex-shrink-0" />
            <p className="text-sm">{banner}</p>
          </div>
        )}

        {/* At-a-glance row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gray-900/60 border-gray-700">
            <CardContent className="pt-5">
              <p className="text-xs text-gray-400">Status</p>
              <Badge variant="outline" className={`mt-2 ${tone.color} text-xs flex items-center gap-1 w-fit`} data-testid="sub-status-badge">
                <StatusIcon className="h-3 w-3" /> {tone.label}
              </Badge>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/60 border-gray-700">
            <CardContent className="pt-5">
              <p className="text-xs text-gray-400">Plan</p>
              <p className="text-lg font-bold text-cyan-400 uppercase mt-2" data-testid="current-package-label">{info.package}</p>
              <p className="text-[11px] text-gray-500 mt-1">Switchable below</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/60 border-gray-700">
            <CardContent className="pt-5">
              <p className="text-xs text-gray-400 flex items-center gap-1"><Calendar className="h-3 w-3" /> Next Due</p>
              <p className="text-base font-semibold text-white mt-2">{dueText}</p>
              <p className="text-[11px] text-gray-500 mt-1">5-day grace period</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/60 border-gray-700">
            <CardContent className="pt-5">
              <p className="text-xs text-gray-400 flex items-center gap-1"><IndianRupee className="h-3 w-3" /> Monthly</p>
              <p className="text-lg font-bold text-white mt-2">{fmtINR(info.monthly_amount)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Change Plan */}
        <Card className="bg-gray-900/60 border-cyan-700/50" data-testid="change-plan-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-cyan-300 text-base flex items-center gap-2">
              <ArrowUpCircle className="h-4 w-4" /> Change Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { id: "core", label: "Core", price: info.pricing_core, features: ["HR & Payroll", "Work Orders", "Store Inventory", "Accounts (basic)", "Quotations + Delivery Challans"] },
                { id: "pro",  label: "Pro",  price: info.pricing_pro,  features: ["Everything in Core", "Cost Calculator", "SPD Display + WO Progress", "Tally XML Export", "Admin Reports & History", "Employee shop-floor portal"] },
              ].map(p => {
                const isCurrent = info.package === p.id;
                return (
                  <div
                    key={p.id}
                    className={`rounded-lg border p-4 ${isCurrent ? "border-emerald-500/60 bg-emerald-500/5" : "border-gray-700 bg-gray-800/40 hover:border-gray-500"}`}
                    data-testid={`plan-card-${p.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-base font-bold text-white uppercase">{p.label}</p>
                        <p className="text-[11px] text-gray-400">{fmtINR(p.price)} / month</p>
                      </div>
                      {isCurrent ? (
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 border" data-testid={`plan-current-${p.id}`}>CURRENT</Badge>
                      ) : (
                        <Button
                          size="sm"
                          disabled={planBusy}
                          onClick={() => changePlan(p.id)}
                          className={p.id === "pro" ? "bg-cyan-600 hover:bg-cyan-700" : "bg-gray-600 hover:bg-gray-700"}
                          data-testid={`switch-to-${p.id}-btn`}
                        >
                          {planBusy ? "Switching…" : (p.id === "pro" && info.package === "core" ? "Upgrade" : p.id === "core" && info.package === "pro" ? "Downgrade" : `Switch to ${p.label}`)}
                        </Button>
                      )}
                    </div>
                    <ul className="mt-3 space-y-1">
                      {p.features.map(f => (
                        <li key={f} className="text-xs text-gray-300 flex items-start gap-2">
                          <CheckCircle2 className={`h-3 w-3 mt-0.5 ${p.id === "pro" ? "text-cyan-400" : "text-emerald-400"}`} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Pricing (master-only edit) */}
        <Card className="bg-gray-900/60 border-blue-700/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-blue-300 text-base flex items-center gap-2">
              <IndianRupee className="h-4 w-4" /> Edit Monthly Pricing
            </CardTitle>
            <p className="text-xs text-gray-400">Set the monthly amount for each package. The active plan's price is what gets recorded when you mark a payment.</p>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-gray-300 text-xs">Core monthly (INR)</Label>
              <Input type="number" min={0} value={priceCore} onChange={(e)=>setPriceCore(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="pricing-core-input" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs">Pro monthly (INR)</Label>
              <Input type="number" min={0} value={pricePro} onChange={(e)=>setPricePro(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="pricing-pro-input" />
            </div>
            <Button disabled={busy} onClick={updatePricing} className="bg-blue-600 hover:bg-blue-700" data-testid="update-pricing-btn">
              Save Pricing
            </Button>
          </CardContent>
        </Card>

        {/* Mark Paid */}
        <Card className="bg-gray-900/60 border-emerald-700/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-emerald-300 text-base flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Record Monthly Payment
            </CardTitle>
            <p className="text-xs text-gray-400">Each payment extends the subscription by exactly 1 month and unlocks the app.</p>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-gray-300 text-xs">Amount received (INR)</Label>
              <Input type="number" min={0} value={paymentAmt} onChange={(e)=>setPaymentAmt(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="payment-amount" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-gray-300 text-xs">Note (optional)</Label>
              <Input value={paymentNote} onChange={(e)=>setPaymentNote(e.target.value)} placeholder="UPI ref / cheque #" className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="payment-note" />
            </div>
            <Button disabled={busy} onClick={markPaid} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mark-paid-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Paid
            </Button>
          </CardContent>
        </Card>

        {/* Manual lock */}
        <Card className="bg-gray-900/60 border-red-700/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-red-300 text-base flex items-center gap-2">
              <Lock className="h-4 w-4" /> Manual Lock Control
            </CardTitle>
            <p className="text-xs text-gray-400">Override automatic state. Useful for instantly cutting off non-payers or extending temporary trial access.</p>
          </CardHeader>
          <CardContent className="flex gap-2 flex-wrap">
            <Button variant="outline" disabled={busy || info.is_locked} onClick={lockApp} className="border-red-500/40 text-red-300 hover:bg-red-500/20" data-testid="manual-lock-btn">
              <Lock className="h-4 w-4 mr-2" /> Lock app
            </Button>
            <Button variant="outline" disabled={busy || !info.is_locked} onClick={unlockApp} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20" data-testid="manual-unlock-btn">
              <Unlock className="h-4 w-4 mr-2" /> Unlock (no payment)
            </Button>
          </CardContent>
        </Card>

        {/* Payment history */}
        <Card className="bg-gray-900/60 border-gray-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-cyan-300 text-base flex items-center gap-2">
              <History className="h-4 w-4" /> Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(info.payment_history || []).length === 0 ? (
              <p className="text-sm text-gray-500">No payments recorded yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-700">
                    <TableHead className="text-gray-400 text-xs">Recorded</TableHead>
                    <TableHead className="text-gray-400 text-xs">For Month</TableHead>
                    <TableHead className="text-gray-400 text-xs">Amount</TableHead>
                    <TableHead className="text-gray-400 text-xs">Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {info.payment_history.map((p, i) => (
                    <TableRow key={i} className="border-gray-800">
                      <TableCell className="text-gray-300 text-xs">{new Date(p.date).toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-gray-200 text-xs">{p.for_month || (p.months ? `${p.months} mo` : "—")}</TableCell>
                      <TableCell className="text-gray-200 text-xs">{p.currency || "INR"} {Number(p.amount || 0).toLocaleString("en-IN")}</TableCell>
                      <TableCell className="text-gray-400 text-xs">{p.note || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Razorpay fee/revenue calculator — master-only (page already gated). */}
        <RazorpayFeesCalculator />

      </div>
    </div>
  );
};

export default AdminSubscription;
